import { Reservation } from 'src/types/reservation'
import useUnmarkAsPurchasedEligible from 'src/routes/(registry)/registry/(gift-tracker-revamp)/hooks/useUnmarkAsPurchasedEligible/useUnmarkAsPurchasedEligible'
import {
  RETURNS_AMAZON_URL,
  RETURNS_BUY_BUY_BABY_URL,
  RETURNS_ETSY_URL,
  RETURNS_TARGET_URL,
  RETURNS_WALMART_URL,
} from 'shared/constants'

export type WriteReviewActionVariant = 'primary' | 'tertiary'

const writeReviewActionVariant = (
  reservation: Reservation
): WriteReviewActionVariant | undefined => {
  if (reservation.regItem?.turntoSku) {
    return 'primary'
  }

  if (reservation.subscriptionUrl) {
    return 'tertiary'
  }

  return undefined
}

const validAmazonOrderNumber = (orderNum: string | null | undefined) =>
  orderNum && /\d{3}-?\d{7}-?\d{7}/.test(orderNum)

const returnInstructionsUrlForReservation = (reservation: Reservation) => {
  switch (reservation.storeName) {
    case 'Amazon':
      return validAmazonOrderNumber(reservation.orderNumber)
        ? `https://www.amazon.com/spr/returns/cart?_encoding=UTF8&orderId=${reservation.orderNumber}`
        : RETURNS_AMAZON_URL
    case 'Walmart':
      return RETURNS_WALMART_URL
    case 'Target':
      return RETURNS_TARGET_URL
    case 'Etsy':
      return RETURNS_ETSY_URL
    case (
      (reservation.storeName &&
        reservation.storeName?.match(/buy\s*buy\s*baby/i)) ||
      {}
    ).input:
      return RETURNS_BUY_BUY_BABY_URL
    default:
      return undefined
  }
}

const useGiftActions = (reservation: Reservation) => {
  const isUnmarkPurchaseEnabled = useUnmarkAsPurchasedEligible(reservation)
  const writeReviewVariant = writeReviewActionVariant(reservation)
  const returnInstructionsUrl = returnInstructionsUrlForReservation(reservation)

  const actionList = {
    isActivateSubscriptionEnabled:
      reservation.isPurchased && !!reservation.subscriptionUrl,
    isCancelReservationEnabled:
      !reservation.isPurchased && !reservation.regItem?.isFavor,
    isGiftCardBalanceEnabled:
      reservation.isPurchased && !!reservation.regItem?.isGiftCard,
    isReturnItemEnabled: reservation.isPurchased && !!reservation.returnWebUrl,
    isReturnInstructionsEnabled:
      reservation.isPurchased &&
      !reservation.orderUuid &&
      reservation.regItem?.itemType === 'product' &&
      !reservation.paymentMethod,
    isUnmarkPurchaseEnabled,
    isWriteReviewEnabled: reservation.isPurchased && !!writeReviewVariant,
  }

  const showGiftActions = Object.values(actionList).some((enabled) => enabled)

  return {
    ...actionList,
    returnInstructionsUrl,
    showGiftActions,
    writeReviewVariant,
  }
}

export default useGiftActions
